import type { Post } from "@/lib/news/types";
import hero from "@/assets/news/lan-som-arbetslos.jpg";

/** Långivare som gör individuell bedömning och accepterar a-kassa, bidrag eller pension. */
const LAG_INKOMST = [
  "credifi", "lumify", "ferratum", "saldo", "loanstep", "creditstar",
  "flexkonto", "merax", "tomly", "brixokk", "northmill", "klaralan", "fairlo",
];

/** Näst bästa alternativen – förmedlare som prövar hos många banker samtidigt. */
const NAST_BAST = ["lendo", "enklare", "sambla", "advisa", "axo"];

export const LAN_SOM_ARBETSLOS: Record<string, Post> = {
  "lan-som-arbetslos-lag-inkomst-seriosa-langivare": {
    title: "Lån som arbetslös 2026 – låg inkomst och seriösa långivare",
    intro:
      "Att stå utan jobb och samtidigt behöva pengar är en av de mest pressande situationer som finns. Vi förstår – räkningarna tar ingen paus bara för att lönen gör det. Här går vi igenom vilka inkomster som faktiskt räknas 2026, vilka seriösa långivare som prövar ansökningar vid låg inkomst, vad ett lån kostar i kronor och vilka gratisalternativ som ofta är ett klokare första steg.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "9 september 2026",
    updated: "9 september 2026",
    heroSrc: hero,
    heroAlt: "Person söker lån som arbetslös vid köksbordet med dator och räkningar",
    topLenderId: "credifi",
    bottomLenderId: "northmill",
    topLenderTitle: "Bäst i test – lån vid låg inkomst 2026",
    bottomLenderTitle: "Vår rekommendation för längre löptid och lägre ränta",
    reviewsLenderId: "northmill",
    cta: { to: "/lan-utan-uc", label: "Jämför seriösa långivare vid låg inkomst" },
    calculator: {
      title: "Räkna på lån som arbetslös",
      intro:
        "Se vad lånet faktiskt kostar innan du ansöker – månadskostnad, räntekostnad, total lånekostnad och effektiv ränta.",
      note:
        "Månadskostnaden styrs av lånebelopp, löptid och nominell ränta, men det är den effektiva räntan som visar din verkliga kostnad eftersom uppläggningsavgift och aviavgift räknas in. Med osäker inkomst är kort löptid och litet belopp nästan alltid tryggare, även om månadskostnaden blir högre.",
      amount: 10000,
      minAmount: 1000,
      maxAmount: 50000,
      months: 12,
      maxMonths: 60,
      rate: 24.9,
      minRate: 5,
      maxRate: 39,
      setupFee: 350,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Kan man få lån som arbetslös?",
        a: "Ja, det förekommer, men det är svårare. Långivaren kräver en regelbunden och dokumenterad inkomst – a-kassa, aktivitetsstöd, sjukpenning eller pension kan räknas hos flera nischlångivare, medan storbankerna oftast kräver anställning.",
      },
      {
        q: "Räknas a-kassa som inkomst?",
        a: "Hos många nischlångivare ja, eftersom ersättningen är regelbunden och går att verifiera. Beloppet vägs mot dina utgifter och befintliga skulder, så en låg a-kassa räcker sällan till större lån.",
      },
      {
        q: "Hur mycket kan jag låna med låg inkomst?",
        a: "Vanligtvis mellan 1 000 och 20 000 kr som ny kund. Kvar-att-leva-på-kalkylen sätter taket, och den tar hänsyn till boendekostnad, försörjningsbörda och befintliga krediter.",
      },
      {
        q: "Finns det långivare som beviljar alla?",
        a: "Nej. Alla långivare med tillstånd från Finansinspektionen måste enligt konsumentkreditlagen göra en kreditprövning. Den som lovar garanterat ja är antingen oseriös eller ett rent bedrägeri.",
      },
      {
        q: "Vad kostar ett lån på 10 000 kr vid låg inkomst?",
        a: "Räntan hamnar ofta runt 25 procent. Med 350 kr i uppläggningsavgift och 29 kr per avi blir månadskostnaden cirka 985 kr på tolv månader och totalkostnaden ungefär 11 800 kr.",
      },
      {
        q: "Är det bättre med en medsökande?",
        a: "Ja, tydligt. Två inkomster sänker den bedömda risken och ger både högre chans till ja och lägre ränta. Kom ihåg att medsökanden blir lika betalningsskyldig som du.",
      },
      {
        q: "Vilka gratisalternativ finns innan jag lånar?",
        a: "Kommunens budget- och skuldrådgivning är kostnadsfri och opartisk. Många långivare och hyresvärdar beviljar också anstånd eller avbetalningsplan om du hör av dig innan förfallodagen.",
      },
      {
        q: "Kan jag ångra lånet?",
        a: "Ja, du har 14 dagars ångerrätt enligt konsumentkreditlagen. Du betalar tillbaka lånebeloppet plus ränta för de dagar du haft pengarna.",
      },
    ],
    blocks: [
      { t: "h2", text: "Lån som arbetslös – kort svar" },
      { t: "p", text: "Ett lån som arbetslös är möjligt om du har en regelbunden ersättning som går att styrka, men chansen är lägre och räntan högre än vid anställning. Flera nischlångivare accepterar a-kassa, aktivitetsstöd, sjukpenning eller pension som inkomst. Storbankerna gör det sällan." },
      { t: "p", text: "Vi tänker inte lova något garanterat godkännande. Vi visar var chansen är störst, vad det kostar och när du bör välja något annat än ett nytt lån." },
      {
        t: "stats",
        items: [
          { label: "Typiskt beviljat belopp", value: "1 000–20 000 kr" },
          { label: "Ränta vid låg inkomst 2026", value: "19–34 %" },
          { label: "Vanligt inkomstkrav", value: "Regelbunden ersättning" },
        ],
      },

      { t: "h2", text: "Vilka inkomster räknas 2026?" },
      { t: "p", text: "Det avgörande är inte varifrån pengarna kommer utan att de är regelbundna och möjliga att verifiera." },
      {
        t: "table",
        caption: "Så bedöms olika inkomstslag",
        head: ["Inkomsttyp", "Accepteras oftast", "Kommentar"],
        rows: [
          ["A-kassa", "Ja hos nischlångivare", "Räknas som regelbunden ersättning"],
          ["Aktivitetsstöd", "Ofta", "Kräver löpande utbetalningar"],
          ["Sjuk- eller aktivitetsersättning", "Ja", "Bedöms som stabil inkomst"],
          ["Pension", "Ja", "Ses ofta som mycket stabil"],
          ["Försörjningsstöd", "Sällan", "Räknas normalt inte"],
          ["Svartjobb eller oregistrerad inkomst", "Nej", "Går inte att styrka"],
        ],
      },
      { t: "p", text: "Uppge alltid samtliga inkomster i ansökan. Många missar bostadsbidrag, barnbidrag och underhållsstöd, vilket sänker den kalkyl långivaren räknar på." },

      { t: "h2", text: "Så gör långivaren kreditprövningen" },
      { t: "p", text: "Kreditprövningen är lagstadgad. Långivaren räknar fram ditt kvar-att-leva-på-belopp och jämför det med Konsumentverkets referensvärden." },
      { t: "h3", text: "Inkomst mot utgifter" },
      { t: "p", text: "Boendekostnad, hushållsutgifter och antal personer i hushållet dras från inkomsten. Det som blir kvar avgör hur stor månadskostnad du klarar." },
      { t: "h3", text: "Befintliga skulder" },
      { t: "p", text: "Alla krediter räknas med, även kontokrediter du inte använder. Säg upp oanvända utrymmen innan du ansöker." },
      { t: "h3", text: "Kreditupplysning" },
      { t: "p", text: "Nischlångivare använder ofta Creditsafe eller Dun & Bradstreet i stället för UC, vilket kan hjälpa dig som redan har många förfrågningar." },

      { t: "h2", text: "Seriösa långivare vid låg inkomst" },
      { t: "p", text: "Nedan listar vi de långivare som gör individuell bedömning och oftast accepterar andra inkomster än lön. Samtliga har tillstånd från Finansinspektionen." },
      {
        t: "lenders",
        title: "Långivare som prövar vid låg inkomst 2026",
        note: "Sorterat efter vår helhetsbedömning av ränta, villkor, utbetalningstid och kundomdömen.",
        ids: LAG_INKOMST,
      },

      { t: "h2", text: "Vad kostar lånet i kronor?" },
      {
        t: "table",
        caption: "Räkneexempel vid 24,9 % ränta, 350 kr uppläggning och 29 kr avi",
        head: ["Belopp", "Löptid", "Månadskostnad", "Total kostnad"],
        rows: [
          ["5 000 kr", "12 mån", "cirka 505 kr", "cirka 6 060 kr"],
          ["10 000 kr", "12 mån", "cirka 985 kr", "cirka 11 800 kr"],
          ["20 000 kr", "24 mån", "cirka 1 100 kr", "cirka 26 400 kr"],
        ],
      },
      { t: "p", text: "**Ett litet lån kan bli dyrt fort.** Med osäker inkomst är risken dessutom stor att löptiden förlängs, och då stiger totalkostnaden ytterligare." },

      { t: "h2", text: "Gratisalternativen du bör pröva först" },
      {
        t: "steps",
        title: "Fem steg innan du ansöker",
        items: [
          "Ring fordringsägaren och begär anstånd eller avbetalningsplan.",
          "Boka tid hos kommunens budget- och skuldrådgivning – den är kostnadsfri.",
          "Kontrollera att du får alla bidrag du har rätt till hos Försäkringskassan.",
          "Säg upp abonnemang och kontokrediter du inte använder.",
          "Undersök om a-kassan kan betala ut i förskott.",
        ],
      },
      { t: "p", text: "Det här är sällan lika snabbt som ett lån, men det kostar inget och förvärrar inte situationen." },

      { t: "h2", text: "Så ökar du chansen att bli beviljad" },
      {
        t: "ul",
        items: [
          "Sök minsta möjliga belopp – inte det maximala du erbjuds.",
          "Vänta 30 dagar efter ett avslag innan du ansöker igen.",
          "Lägg till en medsökande med stabil ekonomi.",
          "Ange all inkomst, inklusive bidrag och ersättningar.",
          "Välj en långivare som prövar utan UC om du har många förfrågningar.",
        ],
      },
      { t: "p", text: "Har du redan flera avslag bakom dig hittar du fler konkreta råd i vår guide om [låna pengar utan avslag](/nyhet/lana-pengar-utan-avslag)." },

      { t: "h2", text: "Lån vid betalningsanmärkning eller skuld hos Kronofogden" },
      { t: "p", text: "Kombinationen låg inkomst och betalningsanmärkning gör det mycket svårt. Är anmärkningen äldre och skulden reglerad gör vissa aktörer ändå en individuell bedömning." },
      { t: "p", text: "Med ett pågående mål hos Kronofogden får du i praktiken avslag överallt. Läs mer i vår genomgång av [lån trots Kronofogden](/nyhet/sms-lan-trots-kronofogden) – och prioritera skuldrådgivning före en ny ansökan." },

      { t: "h2", text: "Så känner du igen en oseriös aktör" },
      {
        t: "box",
        tone: "warning",
        title: "Varningssignaler",
        items: [
          "Löfte om garanterat lån oavsett kreditvärdighet.",
          "Avgift som ska betalas i förskott innan pengarna betalas ut.",
          "Kontakt enbart via sms, WhatsApp eller Messenger.",
          "Inget organisationsnummer, ingen adress och inga fullständiga villkor.",
          "Företaget saknas i Finansinspektionens register.",
        ],
      },

      { t: "h2", text: "Vad lagen säger om kostnaden" },
      { t: "p", text: "Konsumentkreditlagen sätter ett räntetak på styrräntan plus 40 procentenheter och ett kostnadstak som innebär att ränta, avgifter och dröjsmålsränta aldrig får överstiga lånebeloppet." },
      { t: "p", text: "Du har alltid 14 dagars ångerrätt och rätt att lösa lånet i förtid utan extra kostnad på ett lån med rörlig ränta." },

      { t: "h2", text: "Förmedlare kan vara ett bättre första steg" },
      { t: "p", text: "En låneförmedlare skickar din ansökan till många banker med en enda kreditupplysning. Det höjer chansen till ja utan att du samlar på dig förfrågningar." },
      {
        t: "lenders",
        title: "Näst bästa alternativ – förmedlare som prövar hos flera banker",
        note: "Kräver UC, men ger flera bud på en enda förfrågan.",
        ids: NAST_BAST,
      },

      { t: "h2", text: "När du bör avstå helt" },
      { t: "p", text: "Om lånet ska täcka löpande utgifter i stället för en engångskostnad löser det inte problemet – det flyttar bara fram det och lägger på ränta." },
      { t: "p", text: "Samma sak om du redan har flera krediter som ska betalas samma månad. Då är [samla lån](/samla-lan) eller skuldrådgivning en klokare väg." },

      { t: "h2", text: "Så granskar vi långivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, utbetalningstid, villkorens tydlighet, tillstånd hos Finansinspektionen och kundomdömen. Ingen aktör kan köpa en placering. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Lån som arbetslös är möjligt när ersättningen är regelbunden och går att styrka, men beloppen är små och räntan hög. Pröva anstånd, bidrag och kostnadsfri skuldrådgivning först, sök sedan minsta möjliga belopp hos en långivare som gör individuell bedömning – och jämför alltid effektiv ränta innan du signerar." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "Finansinspektionen – tillstånd och tillsyn över konsumentkreditinstitut.",
          "Konsumentverket och Hallå konsument – konsumentkreditlagen, kreditprövning och referensvärden för levnadskostnader.",
          "Kronofogden – information om betalningsanmärkningar och skuldsanering.",
        ],
      },
    ],
  },
};
